import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import "./FormSectionModal.scss";

// Sections and sub-sections in tree order; depth drives the "— " indent, as in the form builder
const flattenSections = (sections, depth = 0) => (sections ?? []).reduce((items, section) => [
	...items,
	{ id: section.id, title: section.title, depth: depth },
	...flattenSections(section.subSections, depth + 1)
], []);

// Picks the destination section for a question (form builder's "Move question" dialog)
function MoveQuestionModal({ t, show, question, fromSectionId, sections, onHide, onMove }) {
	const [targetSectionId, setTargetSectionId] = useState('');

	useEffect(() => {
		if (show) setTargetSectionId('');
	}, [show]);

	const sectionOptions = useMemo(
		() => flattenSections(sections).filter(section => section.id !== fromSectionId),
		[sections, fromSectionId]
	);

	const selectOptions = useMemo(
		() => sectionOptions.map(section => ({ value: section.id, label: section.title, depth: section.depth })),
		[sectionOptions]
	);

	// Match on the plain title so the "— " depth indent doesn't affect search
	const filterSectionOption = (option, input) =>
		(option.data.label ?? '').toLowerCase().includes(input.trim().toLowerCase());

	const formatSectionOption = (option, { context }) =>
		context === 'menu' ? `${'— '.repeat(option.depth)}${option.label}` : option.label;

	const onSubmit = (e) => {
		e.preventDefault();
		if (!targetSectionId) return;
		onMove?.(Number(targetSectionId));
	};

	return (
		<Modal show={show} onHide={onHide} centered className="form-section-modal">
			<Form noValidate onSubmit={onSubmit}>
				<Modal.Header closeButton className="form-section-modal__header">
					<Modal.Title className="form-section-modal__title">{t('MoveQuestion.label')}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="form-section-modal__body">
					<p className="form-section-modal__hint-text">
						{t('MoveItemToAnotherSection.label', { name: question?.name ?? '' })}
					</p>
					<div className="form-section-modal__field">
						<label className="form-section-modal__label" htmlFor="moveQuestionSection">
							{t('DestinationSection.label')}
							<span className="form-section-modal__required" aria-hidden="true">*</span>
						</label>
						<Select inputId="moveQuestionSection" className="form-section-modal__search-select"
							classNamePrefix="form-section-modal__search-select" isSearchable isClearable
							placeholder={t('Select.label')} noOptionsMessage={() => t('noDataAvailable.label')}
							options={selectOptions} filterOption={filterSectionOption} formatOptionLabel={formatSectionOption}
							value={selectOptions.find(option => String(option.value) === String(targetSectionId)) ?? null}
							onChange={(option) => setTargetSectionId(option ? String(option.value) : '')}
							menuPortalTarget={document.body} styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }} />
					</div>
				</Modal.Body>
				<Modal.Footer className="form-section-modal__footer">
					<button type="button" className="form-section-modal__btn form-section-modal__btn--secondary" onClick={onHide}>
						{t('Cancel.label')}
					</button>
					<button type="submit" className="form-section-modal__btn form-section-modal__btn--primary" disabled={!targetSectionId}>
						{t('Metrics.moveTo.move.label')}
					</button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

export default withTranslation()(MoveQuestionModal);
